// src/components/authentication/SignIn.js
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation, Link } from 'react-router-dom';
import { arrayToMap } from '../../security/permissionRegistry';
import { NotificationManager } from 'react-notifications';
import { ArrowRight, Eye, EyeOff, LockKeyhole, Mail, Server, ShieldCheck } from 'lucide-react';

import logo from '../../img/logo_text.svg';
import doctor from '../../img/doctor2.webp';
import googleIcon from '../../img/google-icon.svg';

import { useUser } from '../../UserContext';
import {
  deploymentModes,
  deploymentStorageKeys,
  getDeploymentMode,
  getDeploymentModeOverride,
  isLocalApplianceOrigin,
  isLocalEditionBuild,
} from '../../services/deploymentConfig';
import { localActivationService, normalizeActivationHospital } from '../../services/localActivationService';
import { clearLocalSession, loginLocalApi } from '../../services/localAuthService';

// Route map by module key
const ROUTE_BY_MODULE = {
  dashboard: '/dashboard',
  consultations: '/consultations',
  'nurses-station': '/nurses-station',
  patients: '/patients',
  appointments: '/appointments',
  admission: '/admissions',
  wards: '/wards',
  pharmacy: '/pharmacy/dashboard',
  lab: '/lab/dashboard',
  billing: '/billing/payment',
  inventory: '/pharmacy/inventory',
  schemes: '/schemes/patients',
  'scheme-desk': '/schemes/desk',
  reports: '/reports/registered-patients', // safer default
  'roles-permissions': '/roles-permissions',
  'server-management': '/server-management',
  'sync-status': '/admin/sync-status',
  departments: '/departments',
  clinics: '/clinics',
  settings: '/settings',
};

// Order we’ll try modules to pick a landing page
const MODULE_PRIORITY = [
  'dashboard',
  'consultations',
  'nurses-station',
  'patients',
  'appointments',
  'admission',
  'wards',
  'pharmacy',
  'lab',
  'billing',
  'inventory',
  'schemes',
  'scheme-desk',
  'reports',
  // allow admin-y pages explicitly
  'roles-permissions',
  'server-management',
  'sync-status',
  'departments',
  'clinics',
  'settings',
];

const pickFirstRouteFromPerms = (permMap) => {
  if (!permMap) return null;
  for (const key of MODULE_PRIORITY) {
    if (permMap?.[key]?.view === true && ROUTE_BY_MODULE[key]) {
      return ROUTE_BY_MODULE[key];
    }
  }
  return null;
};

const hasAnyTrue = (permMap) => {
  if (!permMap || typeof permMap !== 'object') return false;
  return Object.values(permMap).some(
    (mod) => mod && typeof mod === 'object' && Object.values(mod).some((v) => v === true)
  );
};

// Try to construct a permission map from whatever we can find
const buildPermissionMap = async (user) => {
  // 1) prefer v2 map on user record
  if (user?.permissionsV2 && typeof user.permissionsV2 === 'object' && hasAnyTrue(user.permissionsV2)) {
    return user.permissionsV2;
  }
  // 2) legacy array on user record
  if (Array.isArray(user?.permissions) && user.permissions.length) {
    return arrayToMap(user.permissions);
  }
  // 3) last resort: fetch legacy permissions by role
  if (user?.hospitalId && user?.roleName) {
    const { fetchRolePermissions } = await import('../../firebase/RoleManagementService');
    const arr = await fetchRolePermissions(user.hospitalId, user.roleName);
    if (Array.isArray(arr) && arr.length) {
      return arrayToMap(arr);
    }
  }
  return null;
};

const isBrowserLocalHost = () => {
  if (typeof window === 'undefined') return false;
  return ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
};

const isDevServerOrigin = () => {
  if (typeof window === 'undefined') return false;
  if (!isBrowserLocalHost()) return false;
  return ['3000', '3001'].includes(String(window.location.port || ''));
};

const clearCloudConsoleSession = () => {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(deploymentStorageKeys.cloudFacilitySession);
  window.sessionStorage.removeItem('platformUserData');
};

const SignIn = () => {
  const { setUser, setHospitalId, setHospitalDetails } = useUser();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [deploymentMode, setDeploymentMode] = useState(() => getDeploymentMode());
  const [activationStatus, setActivationStatus] = useState(null);
  const [isActivationLoading, setIsActivationLoading] = useState(false);
  const [activationChecked, setActivationChecked] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const mode = params.get('mode');
    const oobCode = params.get('oobCode');

    if (mode === 'resetPassword' && oobCode) {
      history.replace(`/set-new-password?${params.toString()}`);
    }
  }, [history, location.search]);

  const explicitLocalOverride =
    getDeploymentModeOverride() === deploymentModes.OFFLINE || getDeploymentModeOverride() === deploymentModes.HYBRID;
  const localModeFromDeployment = deploymentMode === deploymentModes.OFFLINE || deploymentMode === deploymentModes.HYBRID;
  const applianceOrigin = isLocalApplianceOrigin();
  const localShell = isLocalEditionBuild() || explicitLocalOverride || (!isDevServerOrigin() && localModeFromDeployment);
  const shouldProbeLocalServer = localShell || applianceOrigin;
  const activatedLocalMode =
    activationStatus?.activated &&
    (activationStatus?.deploymentMode === deploymentModes.OFFLINE || activationStatus?.deploymentMode === deploymentModes.HYBRID);
  const isPreActivationAppliance = applianceOrigin && activationChecked && !activationStatus?.activated;
  const isLocalMode = Boolean(activatedLocalMode);
  const activeHospital = normalizeActivationHospital(activationStatus || {});
  const environmentLabel = isLocalMode
    ? activationStatus?.deploymentMode === deploymentModes.HYBRID || deploymentMode === deploymentModes.HYBRID
      ? 'Hybrid Local Server'
      : 'Local Edition'
    : isPreActivationAppliance
      ? 'Online Activation'
    : 'Online Cloud';

  useEffect(() => {
    let mounted = true;
    const checkActivation = async () => {
      if (!shouldProbeLocalServer) {
        setActivationChecked(true);
        return;
      }
      setIsActivationLoading(true);
      try {
        const status = await localActivationService.status();
        if (!mounted) return;
        setActivationStatus(status);
        if (status?.activated) {
          setDeploymentMode(status.deploymentMode || getDeploymentMode());
          sessionStorage.setItem('hospitalId', status.hospitalId || '');
          sessionStorage.setItem('hospitalDetails', JSON.stringify(normalizeActivationHospital(status)));
        } else if (localShell && !applianceOrigin) {
          history.replace('/activate-local');
        }
      } catch (err) {
        if (!mounted) return;
        if (isLocalEditionBuild() || deploymentMode === deploymentModes.OFFLINE || deploymentMode === deploymentModes.HYBRID) {
          setError(err?.message || 'Local API is unavailable. Start the ClearHealth Local Server and try again.');
        }
      } finally {
        if (mounted) {
          setIsActivationLoading(false);
          setActivationChecked(true);
        }
      }
    };
    checkActivation();
    return () => {
      mounted = false;
    };
  }, [applianceOrigin, deploymentMode, history, localShell, shouldProbeLocalServer]);

  const postLogin = async (userEmail, options = {}) => {
    const [
      { fetchUsersByEmail },
      { fetchHospitalSettings },
      { isOperationsUser },
    ] = await Promise.all([
      import('../../firebase/StaffManagementService'),
      import('../../firebase/GeneralSettingsServices'),
      import('../../firebase/OperationsConsoleService'),
    ]);

    // Load profile from Firestore
    const profile = await fetchUsersByEmail(userEmail);
    if (!profile || (Array.isArray(profile) && profile.length === 0)) {
      throw Object.assign(new Error('User record not found in database'), { code: 'app/profile-missing' });
    }
    const userData = Array.isArray(profile) ? profile[0] : profile;

    // Set session/context
    setUser(userData);
    sessionStorage.setItem('userData', JSON.stringify(userData));

    if (options.redirectAfterProfile) {
      history.push(options.redirectAfterProfile);
      return;
    }

    // Force first-time password update before any dashboard or operations-console access.
    if (userData.needsPasswordChange) {
      setHospitalId(userData.hospitalId || '');
      if (userData.hospitalId) {
        sessionStorage.setItem('hospitalId', userData.hospitalId);
      } else {
        sessionStorage.removeItem('hospitalId');
        setHospitalDetails(null);
        sessionStorage.removeItem('hospitalDetails');
      }
      history.push('/password-update');
      return;
    }

    if (isOperationsUser(userData) && !userData.hospitalId) {
      setHospitalId('');
      sessionStorage.removeItem('hospitalId');
      setHospitalDetails(null);
      sessionStorage.removeItem('hospitalDetails');
      history.push('/operations-console');
      return;
    }

    setHospitalId(userData.hospitalId);
    sessionStorage.setItem('hospitalId', userData.hospitalId || '');

    // Org branding/settings
    const hospitalSettings = await fetchHospitalSettings(userData.hospitalId);
    if (hospitalSettings) setHospitalDetails(hospitalSettings);

    if (isOperationsUser(userData)) {
      history.push('/operations-console');
      return;
    }

    // Admin/Owner/SuperAdmin → dashboard
    if (
      userData.isOwner ||
      userData.isSuperAdmin ||
      userData.roleName === 'Admin' ||
      userData.roleId === 1
    ) {
      history.push('/dashboard');
      return;
    }

    // Build permission map (v2 → legacy → fetch fallback)
    const permMap = await buildPermissionMap(userData);
    const firstRoute = pickFirstRouteFromPerms(permMap);

    // Safe fallback is dashboard, not unauthorized
    history.push(firstRoute || '/dashboard');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      if (shouldProbeLocalServer && !activationChecked) {
        setError('Checking local server activation. Please try again in a moment.');
        return;
      }
      if (isLocalMode) {
        const selectedHospitalId = activationStatus?.hospitalId;
        if (!selectedHospitalId) {
          setError('Activate ClearHealth Local Server before login.');
          history.push('/activate-local');
          return;
        }
        clearCloudConsoleSession();
        const session = await loginLocalApi(email, password, selectedHospitalId);
        setUser(session.user);
        setHospitalId(session.hospitalId);
        setHospitalDetails(session.hospital);
        sessionStorage.setItem('userData', JSON.stringify(session.user));
        sessionStorage.setItem('hospitalId', session.hospitalId);
        sessionStorage.setItem('hospitalDetails', JSON.stringify(session.hospital));

        const firstRoute = pickFirstRouteFromPerms(session.permissionsV2 || session.user.permissionsV2);
        history.push(firstRoute || '/dashboard');
        return;
      }

      clearLocalSession();
      const { signInWithEmail } = await import('../../firebase/authService');
      await signInWithEmail(email, password);
      await postLogin(email, {
        redirectAfterProfile: isPreActivationAppliance ? '/activate-local' : '',
      });

    } catch (err) {
      switch (err.code) {
        case 'app/profile-missing':
          setError('No user profile found for this email.');
          break;
        case 'auth/invalid-credential':
        case 'auth/user-not-found':
        case 'auth/wrong-password':
          setError('Invalid email or password.');
          break;
        case 'auth/user-disabled':
          setError('This user account has been disabled.');
          break;
        case 'auth/too-many-requests':
          setError(
            'Access to this account has been temporarily disabled due to too many failed login attempts. Please try again later.'
          );
          break;
        case 'auth/network-request-failed':
          setError('Check your network connection.');
          break;
        default:
          setError(err?.message || 'An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    if (isLocalMode) {
      setError('Google sign-in is only available in ONLINE mode.');
      return;
    }
    try {
      const { signInWithGoogle } = await import('../../firebase/authService');
      const cred = await signInWithGoogle();
      const userEmail = cred?.user?.email || cred?.email;
      NotificationManager.info('Sign in successful!', userEmail || '');
      if (userEmail) {
        await postLogin(userEmail, {
          redirectAfterProfile: isPreActivationAppliance ? '/activate-local' : '',
        });
      } else {
        // If we can’t resolve email for some reason, still land somewhere valid
        history.push('/dashboard');
      }
    } catch (err) {
      setError('Google Sign-In failed. Try again.');
      // eslint-disable-next-line no-console
      console.error('Error during Google Sign-In:', err?.message || err);
    }
  };

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#edf4fb_0%,#f7fbff_45%,#ffffff_100%)]">
      <div className="grid min-h-screen lg:grid-cols-[minmax(0,560px)_1fr]">
        <div className="flex items-center justify-center px-6 py-10 sm:px-8 lg:px-12">
          <div className="w-full max-w-md">
            <Link
              to="/"
              className="mb-8 inline-flex items-center gap-3 rounded-full border border-slate-200 bg-white/80 px-4 py-2 text-sm font-medium text-slate-600 shadow-sm backdrop-blur transition hover:text-slate-900"
            >
              <img src={logo} alt="ClearHealth Logo" className="h-7 w-auto" />
            </Link>

            <div className="rounded-[32px] border border-slate-200 bg-white/92 p-8 shadow-[0_25px_70px_rgba(15,23,42,0.10)] backdrop-blur sm:p-9">
              <div className="mb-8">
                <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-primary">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  {environmentLabel}
                </div>
                <h1 className="mt-5 text-3xl font-bold tracking-tight text-slate-900">
                  Login to ClearHealth
                </h1>
                <p className="mt-3 text-sm leading-6 text-slate-500">
                  {isLocalMode && activeHospital.hospitalId
                    ? `Connected to: ${activeHospital.hospitalName}`
                    : isPreActivationAppliance
                      ? 'Sign in online once to verify access, then enter the activation code for this local server.'
                    : 'Access your hospital workspace to continue with consultations, billing, diagnostics, pharmacy, and daily operations.'}
                </p>
              </div>

              <form className="space-y-5" onSubmit={handleLogin}>
                {isLocalMode && activeHospital.hospitalId ? (
                  <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                    <Server className="h-5 w-5 text-primary" />
                    <div>
                      <p className="font-semibold text-slate-900">{activeHospital.hospitalName}</p>
                      <p className="text-xs text-slate-500">Mode: {environmentLabel}</p>
                    </div>
                  </div>
                ) : null}

                <div className="space-y-4">
                  <div>
                    <label htmlFor="email" className="mb-2 block text-sm font-medium text-slate-700">
                      Email / phone / username
                    </label>
                    <div className="relative">
                      <Mail className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                      <input
                        id="email"
                        type="text"
                        required
                        autoComplete="username"
                        className="block w-full rounded-2xl border border-slate-200 bg-white py-3 pl-12 pr-4 text-slate-800 placeholder-slate-400 outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                        placeholder="Enter your username"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="password" className="mb-2 block text-sm font-medium text-slate-700">
                      Password
                    </label>
                    <div className="relative">
                      <LockKeyhole className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        required
                        autoComplete="current-password"
                        className="block w-full rounded-2xl border border-slate-200 bg-white py-3 pl-12 pr-12 text-slate-800 placeholder-slate-400 outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={isLoading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                        className="absolute right-4 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                </div>

                {error ? (
                  <div aria-live="polite" className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                    {error}
                  </div>
                ) : null}

                <button
                  type="submit"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-primary px-4 py-3.5 text-sm font-semibold text-white transition hover:bg-primary_light focus:outline-none focus:ring-4 focus:ring-primary/15 disabled:cursor-not-allowed disabled:opacity-70"
                  disabled={isLoading || isActivationLoading || (shouldProbeLocalServer && !activationChecked)}
                >
                  {isLoading || isActivationLoading ? (
                    <>
                      <i className="fas fa-circle-notch fa-spin" />
                      {isActivationLoading ? 'Checking server...' : 'Signing in...'}
                    </>
                  ) : (
                    <>
                      Sign In
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>

                {!isLocalMode && (
                  <>
                    <div className="flex items-center gap-3">
                      <div className="h-px flex-1 bg-slate-200" />
                      <span className="text-xs font-medium uppercase tracking-[0.16em] text-slate-400">
                        or continue with
                      </span>
                      <div className="h-px flex-1 bg-slate-200" />
                    </div>

                    <button
                      type="button"
                      className="inline-flex w-full items-center justify-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-70"
                      onClick={handleGoogleSignIn}
                      disabled={isLoading || isActivationLoading}
                    >
                      <img src={googleIcon} alt="Google Logo" className="h-5 w-5" />
                      Sign in with Google
                    </button>
                  </>
                )}

                <div className="flex flex-col items-center gap-3 border-t border-slate-100 pt-4 text-center">
                  {!isLocalMode ? (
                    <p className="text-sm text-slate-600">
                      Forgot your password?{' '}
                      <Link to="/reset-password" className="font-semibold text-primary transition hover:underline">
                        Reset it here
                      </Link>
                    </p>
                  ) : null}
                  <p className="text-xs leading-5 text-slate-400">
                    {isLocalMode
                      ? 'Local Edition signs in against the hospital server and works without Firebase.'
                      : 'If you do not yet have access, contact your hospital administrator.'}
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="relative hidden overflow-hidden lg:block">
          <img src={doctor} alt="ClearHealth clinician workspace" className="h-full w-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(3,15,32,0.82)_0%,rgba(10,60,121,0.50)_45%,rgba(3,15,32,0.78)_100%)]" />

          <div className="absolute inset-x-0 top-0 p-10 xl:p-12">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm font-medium text-white/90 backdrop-blur">
              <ShieldCheck className="h-4 w-4" />
              ClearHealth EMR
            </div>
          </div>

          <div className="absolute inset-x-0 bottom-0 p-10 xl:p-12">
            <div className="max-w-2xl">
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-blue-100">
                Connected care delivery
              </p>
              <h2 className="mt-4 text-4xl font-semibold tracking-tight text-white xl:text-5xl">
                One platform for clinical care, diagnostics, pharmacy, and hospital operations.
              </h2>
              <p className="mt-5 max-w-xl text-base leading-7 text-white">
                ClearHealth helps hospitals coordinate registration, consultation, service requests,
                medication fulfilment, ward continuity, and billing from one shared workflow.
              </p>
            </div>

            <div className="mt-8 grid gap-3 sm:grid-cols-3">
              {[
                'Clinical workflow continuity',
                'Billing and cashier control',
                'Offline-ready hospital operations',
              ].map((item) => (
                <div
                  key={item}
                  className="rounded-2xl border border-white/12 bg-white/10 px-4 py-4 text-sm font-medium text-white/90 backdrop-blur"
                >
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
